/***************************************************
 * src/LoginForm.js
 ***************************************************/
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./App";
import { GoogleLogin } from "@react-oauth/google";
import ReCAPTCHA from "react-google-recaptcha";
import "./LoginForm.css";

function LoginForm({ isDarkMode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // Determine theme for reCAPTCHA
  const recaptchaTheme = isDarkMode ? "dark" : "light";

  // Show error
  const displayError = (msg) => {
    setErrorMessage(msg);
    setFadeOut(false);
    setTimeout(() => setFadeOut(true), 15000);
    setTimeout(() => setErrorMessage(""), 20000);
  };

  // reCAPTCHA callback
  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Normal email/password
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      displayError("Email and password are required.");
      return;
    }

    // Ensure user completed reCAPTCHA
    if (!recaptchaToken) {
      displayError("Please complete the reCAPTCHA challenge.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          recaptchaToken,
        }),
      });
      const result = await response.json();

      if (response.ok) {
        const { token, user } = result;
        if (token && user) {
          localStorage.setItem("authToken", token);
          login(token);
          navigate("/survey");
        } else {
          displayError("Invalid login response. Please contact support.");
        }
      } else {
        displayError(result.error || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Error during login:", error);
      displayError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /***************************************************
   * Google Login
   ***************************************************/
  const handleGoogleSuccess = async (credentialResponse) => {
    const googleJwt = credentialResponse.credential;
    if (!googleJwt) {
      displayError("Google sign-in returned no credential.");
      return;
    }
    try {
      // POST to /auth/google with mode=login
      const res = await fetch("/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "login",
          token: googleJwt,
        }),
      });
      const data = await res.json();

      if (res.ok && data.token) {
        localStorage.setItem("authToken", data.token);
        login(data.token);
        navigate("/survey");
      } else {
        displayError(data.error || "Google sign-in failed. No token in response.");
      }
    } catch (error) {
      console.error("Google Sign-in Error:", error);
      displayError("Google sign-in error occurred.");
    }
  };

  const handleGoogleError = () => {
    displayError("Google Login Failed");
  };

  return (
    <div className="login-form-container">
      <form onSubmit={handleSubmit} className="login-form">
        <h2>Login</h2>

        <label htmlFor="email">Email Address</label>
        <input
          type="email"
          id="email"
          name="email"
          aria-label="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email Address"
          autoComplete="email"
          required
        />

        <label htmlFor="password">Password</label>
        <input
          type="password"
          id="password"
          name="password"
          aria-label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoComplete="current-password"
          required
        />

        <div className="recaptcha-container" style={{ margin: "1rem 0" }}>
          {/* 
            key={recaptchaTheme} forces the ReCAPTCHA widget 
            to fully remount when theme changes
          */}
          <ReCAPTCHA
            key={recaptchaTheme}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={onRecaptchaChange}
            theme={recaptchaTheme}
          />
        </div>

        {isLoading ? (
          <div className="spinner">Loading...</div>
        ) : (
          <button type="submit" className="submit-btn">
            Log In
          </button>
        )}

        {errorMessage && (
          <p className={`error-message ${fadeOut ? "fade" : ""}`}>
            {errorMessage}
          </p>
        )}
      </form>

      {/* Google Sign-In button */}
      <div style={{ marginTop: "1rem" }}>
        <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
      </div>
    </div>
  );
}

export default LoginForm;
